/*
 * Copyright Deskshare Inc.
 * All rights reserved.
 *
 * This software is proprietary and confidential.
 * Unauthorized copying, distribution, or use is strictly prohibited.
 */

import * as Sentry from "@sentry/react";

import { AddLangToPath } from "@/helper/AddLanguagePath";
import { stripLanguagePrefix } from "@/utils/localizedPath";
import type { CartItem } from "@/utils/cartTypes";

// Site Detection

/**
 * Returns true when the hostname is an RFC-1918 private IPv4 address.
 *
 * Covers all three private ranges (RFC 1918):
 *   10.0.0.0/8       — starts with "10."
 *   172.16.0.0/12    — starts with "172." and second octet 16–31
 *   192.168.0.0/16   — starts with "192.168."
 *
 * Docker's default bridge network sits at 172.17.x.x (inside the 16–31 band),
 * so this correctly identifies container addresses as local.
 * A public 172.x address outside the 16–31 band is correctly excluded.
 */
function isPrivateIpv4(hostname: string): boolean {
  if (hostname.startsWith("10.")) return true;
  if (hostname.startsWith("192.168.")) return true;
  if (hostname.startsWith("172.")) {
    const parts = hostname.split(".");
    const second = parseInt(parts[1] ?? "", 10);
    return second >= 16 && second <= 31;
  }
  return false;
}

/**
 * Check if the current site is the main Deskshare marketing site.
 *
 * Returns true for:
 *   - deskshare.com and www.deskshare.com (production)
 *   - Any *.deskshare.com subdomain EXCEPT api.deskshare.com, *.api.deskshare.com,
 *     shop.deskshare.com, and *.shop.deskshare.com (staging hosts)
 *   - localhost (developer machines)
 *   - All three RFC 1918 private IPv4 ranges (developer machines and Docker containers)
 *
 * This ensures staging and development hosts route sign-in to /auth (merged
 * login) rather than falling through to shop behaviour.
 * The api and shop subdomains are explicitly excluded so they keep their own routing.
 *
 * Item 22 fix: previously only matched the two production hostnames, so a staging
 * host was treated as "not the marketing site" and its sign-in entry resolved to
 * the shop instead of /auth.
 *
 * SC-3: Dev/staging override — set VITE_FORCE_DESKSHARE_CART=true in .env.local
 * to test the Deskshare store path without deploying to deskshare.com.
 * Production builds leave this unset (defaults to false).
 */
export function isDeskshare(): boolean {
  const forceCart = import.meta.env.VITE_FORCE_DESKSHARE_CART;
  if (forceCart === "true") return true;
  if (forceCart === "false") return false;

  try {
    // Strip port so "localhost:5173" and "172.17.0.2:8080" match correctly.
    const current = new URL(window.location.href);
    const hostname = current.hostname;

    // Local development
    if (hostname === "localhost" || hostname === "127.0.0.1") return true;

    // RFC 1918 private ranges (developer machines and Docker containers)
    if (isPrivateIpv4(hostname)) return true;

    // Production hosts
    if (hostname === "deskshare.com" || hostname === "www.deskshare.com")
      return true;

    // Staging subdomains: any *.deskshare.com EXCEPT api and shop subdomains.
    // The explicit deny-list prevents api.deskshare.com and shop.deskshare.com
    // from matching the broad subdomain pattern.
    if (hostname.endsWith(".deskshare.com")) {
      if (
        hostname === "api.deskshare.com" ||
        hostname.endsWith(".api.deskshare.com") ||
        hostname === "shop.deskshare.com" ||
        hostname.endsWith(".shop.deskshare.com")
      ) {
        return false;
      }
      return true;
    }

    return false;
  } catch (error) {
    // URL construction from window.location.href should never fail in a browser,
    // so report this as an unexpected error if it occurs.
    //
    // Session 383 A-1: reported through the MODULE, the way every other file in
    // this application reports. This used to read `window.Sentry?.…`, and
    // nothing in this application ever puts Sentry on `window` — it is
    // installed as a package and started in main.tsx. Only the script-tag build
    // attaches itself to the global object, and that is not what is used here,
    // so the optional chain was always undefined and the report was never sent.
    Sentry.captureException(error);
    return false;
  }
}

// URL Helpers

/**
 * Get the JWT-consumer shopping cart URL.
 *
 * Decision 2 (approved): this variable names the SINGLE origin that validates
 * Deskshare JWTs. The fallback is https://shop.deskshare.com (the new shop),
 * not https://store.deskshare.com (the legacy ASP.NET account site).
 *
 * Why: the JWT audience (aud claim) and this URL must be identical strings.
 * store.deskshare.com does not validate Deskshare tokens; using it as the
 * audience causes every token request to return 400, dropping auth silently
 * on all three sign-in paths (password login, reset, Google OAuth).
 *
 * Set VITE_REACT_APP_SHOPPING_CART_URL=https://shop.deskshare.com in .env
 * and match it exactly with ALLOWED_JWT_AUDIENCES in the backend .env.
 *
 * @returns The base shopping cart / JWT-consumer URL
 */
export function getStoreBaseUrl(): string {
  return (
    import.meta.env.VITE_REACT_APP_SHOPPING_CART_URL ||
    "https://shop.deskshare.com"
  );
}

/**
 * Get the legacy ASP.NET store URL for account/dashboard links on isDeskshare()
 * marketing hosts. This is a separate variable because the legacy store uses
 * session cookies (not JWT tokens), so it is a different destination from the
 * JWT-consuming shop.
 *
 * T2 (interim): the legacy store URL must come from the environment.
 * The previous hardcoded https://store.deskshare.com fallback meant a
 * misconfigured environment silently routed visitors to the live legacy
 * store rather than failing visibly. Returning an empty string surfaces
 * the misconfiguration and triggers a Sentry warning instead of hiding it.
 *
 * This whole function is removed when the legacy store is retired, which is
 * planned for THIS QUARTER — see LEGACY_STORE_RETIREMENT below.
 *
 * Session 383 S-3: the comment here used to cite a handoff section for the
 * retirement timing. The handoff is rewritten every round and had no such
 * section any more, which is exactly how the date came to be carried forward
 * with nowhere left to look it up. The date lives in this file now.
 *
 * @returns The legacy store base URL, or "" when the env var is unset
 */
export function getLegacyStoreUrl(): string {
  const url = import.meta.env.VITE_REACT_APP_LEGACY_STORE_URL;
  if (!url) {
    // Session 383 A-1 (CRITICAL). This report has never once been sent.
    //
    // It used to read `window.Sentry?.captureMessage(...)`. Error reporting in
    // this application is installed as a package and started in main.tsx; it is
    // never placed on `window`. Only the script-tag build of the library does
    // that. So the optional chain was always undefined, and the comment that
    // said this "surfaces the misconfiguration instead of hiding it" described
    // the opposite of what happened.
    //
    // This is not a theoretical gap. An unset value makes every legacy link a
    // RELATIVE path on this site, which the /:productSlug route captures and
    // reports as a failed page load — the broken cart icon in S-3. This one
    // message would have named the cause in five minutes.
    Sentry.captureMessage(
      "VITE_REACT_APP_LEGACY_STORE_URL is not set - legacy store links resolve to " +
        "relative paths on this site and are captured by the product route",
      "error",
    );
    return "";
  }
  return String(url).replace(/\/$/, "");
}

/**
 * When the legacy ASP.NET store is retired, and everything that goes with it.
 *
 * Recorded here rather than in the handoff because the handoff is replaced
 * every round: a date that lives only in a rewritten document, or only in a
 * comment pointing at one, is a date that gets lost. It has already been lost
 * once (Session 383 S-3).
 *
 * When this passes: getLegacyStoreUrl(), hasLegacyStore(), the legacy branches
 * of getAddToCartUrl(), getAddToCartBulkUrl() and getPlanBuyNowUrl(), the
 * Security Monitor Pro camera-tier links, and the
 * VITE_REACT_APP_LEGACY_STORE_URL environment value all go together.
 */
export const LEGACY_STORE_RETIREMENT =
  "Q4 2026 - confirm the exact date with the Shopping Cart team";

/**
 * True when the legacy store address is configured.
 *
 * Session 383 S-3. An unset address makes every legacy link a relative path on
 * this site, which the /:productSlug route captures and reports as a failed
 * page load — the fault reported as a broken cart icon. A caller whose address
 * comes from the legacy store must render a disabled control when this is
 * false, never a link.
 *
 * SCOPE, DECIDED: the four remaining legacy builders — getAddToCartUrl,
 * getAddToCartBulkUrl, the camera-tier links on the Security Monitor Pro
 * purchase page, and the legacy branch of getPlanBuyNowUrl — are deliberately
 * NOT being reworked to use this. Each carries the identical failure and each
 * disappears with getLegacyStoreUrl() at the retirement above. Reworking four
 * call sites, their disabled states and their tests, weeks before all of it is
 * deleted, is work thrown away. The guard exists now because it is one small
 * function, it is what the cart fix is measured against, and it is ready if the
 * retirement slips. Until then, what protects those four is
 * VITE_REACT_APP_LEGACY_STORE_URL being set on every environment — which, after
 * A-1, is finally reported when it is not.
 */
export function hasLegacyStore(): boolean {
  return getLegacyStoreUrl() !== "";
}

/**
 * The legacy store's account page, in the customer's own language.
 *
 * A-3: this used to send lang=eng for everybody. A customer reading the site in
 * German clicked My Account and landed on an English page — at the point where
 * they manage licences and billing, which is exactly where being unable to read
 * the page matters most. The store already accepts a language parameter and the
 * site already holds the mapping; only this call was not using it.
 *
 * T-2: legacyAccountUrl was deleted here. It built the legacy ASP.NET account
 * page address, and after T-2 nothing calls it — My Account goes to the shop
 * dashboard on every host. It is deleted rather than left in place because a
 * dead function that reports a misconfiguration to Sentry keeps reporting a
 * problem with a link that no longer exists.
 *
 * VITE_REACT_APP_LEGACY_STORE_URL is NOT removed with it. The Session 318
 * correction document says this change leaves that variable unused; it does
 * not. getLegacyStoreUrl() still builds the legacy add-to-cart address, the
 * legacy basket address, the bulk add-to-cart address and the Security Monitor
 * Pro camera-tier links. Removing the variable would break legacy checkout on
 * every one of them.
 */

/**
 * The shop dashboard overview URL — the JWT consumer.
 *
 * My Account on Deskshare marketing hosts and on the shop itself both land
 * here. Any caller navigating cross-origin from a Deskshare host must go
 * through completeLoginRedirect() so the shop receives ?authToken= and the
 * customer arrives signed in. A plain anchor lands them signed out.
 *
 * @param language - The current language code (e.g., 'en', 'de')
 */
export function getShopDashboardUrl(language: string = "en"): string {
  const storeUrl = getStoreBaseUrl().replace(/\/$/, "");
  return `${storeUrl}/${language}/dashboard/overview`;
}

/**
 * Always the shopping cart dashboard.
 *
 * T-2: this used to branch on the host and send marketing-host visitors to the
 * legacy store account page. That page has its own session rather than a
 * Deskshare token, and when the legacy address was unset the link resolved to
 * the marketing site itself — which is the behaviour that was reported.
 *
 * @param language - The current language code (e.g., 'en', 'de')
 * @returns The shop dashboard URL
 */
export function getAccountUrl(language: string = "en"): string {
  return getShopDashboardUrl(language);
}

/**
 * Merged auth login URL on the marketing site (/auth with optional lang prefix).
 * Shop hosts keep using getDashboardUrl() for sign-in entry.
 */
export function getAuthLoginUrl(): string {
  return AddLangToPath("/auth");
}

/**
 * True when `pathname` (after stripping an optional /:lang/ prefix) is part
 * of the sign-in flow itself — /auth or /sign-in and their sub-routes.
 *
 * Used by getSignInEntryUrl() below: a customer already viewing one of these
 * pages must never be sent back to it after signing in, or they would land
 * back on the form they just completed and be asked to fill it in again.
 *
 * V-8 item 4: the prefix list used to be URL_LANGUAGES, which omits English.
 * The report described the gap as a missing Italian prefix; Italian was never
 * missing — English was, from two of the three copies of this rule. The
 * scenario the report gave for it (a visitor on /en/auth) is not reachable,
 * because no /en/ route is registered anywhere in the router. The reachable
 * half of the same defect is the copy in safeRedirect.ts, which reads a
 * ?redirect= value rather than the current address; both copies now share
 * stripLanguagePrefix(), so both recognise English.
 */
export function isSignInFlowPath(pathname: string): boolean {
  const stripped = stripLanguagePrefix(pathname);
  return (
    stripped === "/auth" ||
    stripped.startsWith("/auth/") ||
    stripped === "/sign-in" ||
    stripped.startsWith("/sign-in/")
  );
}

/**
 * True when `pathname` is the product support page, in any language.
 *
 * Both spellings are recognised. The route is registered as /product-support,
 * and the header, the footer, the refund policy page, the contact page, the
 * articles page and the uninstall feedback page link to it with and without a
 * trailing slash; the router treats the two as one route. A helper matching
 * only one of them would show Deskshare Support to visitors arriving
 * from some links and not others, with nothing on the page to explain the
 * difference.
 *
 * There is no import cycle: localizedPath.ts imports nothing but the constants
 * file.
 *
 * /priority-support redirects here, so it is covered with no extra work — the
 * widget appears after the redirect, which is correct.
 */
export function isProductSupportPath(pathname: string): boolean {
  const p = stripLanguagePrefix(pathname);
  return p === "/product-support" || p === "/product-support/";
}

/**
 * Session 302 (T-1/T-2): build the href for the header's Sign In control.
 *
 * On Deskshare-operated hosts, sign-in always goes to /auth, carrying the
 * current page as ?redirect= so a successful sign-in returns the customer to
 * the page they started from, rather than the shop dashboard — which is
 * /auth's own fallback (see Login.tsx) when no redirect is present.
 *
 * A customer already on a sign-in page (the /sign-in forwarding page, or
 * /auth itself) gets no redirect param — see isSignInFlowPath() above.
 *
 * Non-Deskshare hosts (the shop) are unaffected: sign-in there already goes
 * straight to the shop's own dashboard via getDashboardUrl().
 *
 * @param language - The current language code, used only for the non-Deskshare branch
 */
export function getSignInEntryUrl(language: string = "en"): string {
  if (!isDeskshare()) return getDashboardUrl(language);

  const authPath = getAuthLoginUrl();
  const { pathname, search } = window.location;
  if (isSignInFlowPath(pathname)) return authPath;

  const current = `${pathname}${search}`;
  return `${authPath}?redirect=${encodeURIComponent(current)}`;
}

/**
 * PH-1 (Session 447): the markers that bring a customer back INTO the chat
 * after a sign-in started from inside it.
 *
 * Two, not one, because they act on two different things. The first is read
 * by this site, on the product support page, and reopens the panel. The
 * second is passed on to the chat on its frame address and asks it to open
 * the past-conversations drawer.
 *
 * Both are ordinary query parameters, so anyone can put them in a link and
 * cause the chat panel to open for a visitor who follows it. That is
 * harmless - opening a chat panel exposes nothing and performs no action -
 * and it is worth having considered rather than discovered. A marker that
 * caused a state change or revealed content would need a guard; neither of
 * these does.
 */
export const CHAT_RETURN_OPEN_PARAM = "deskshare-chat";
export const CHAT_RETURN_OPEN_VALUE = "open";
/**
 * SU-1: reopen at the maximized size the customer had chosen. The sign-in
 * round trip is a full page load, so the panel's size lives only in this
 * marker; without it the chat came back at the normal size. "open" keeps
 * meaning the normal size, which is also what the support chat's own return
 * address asks for, so that address keeps working unchanged.
 */
export const CHAT_RETURN_MAXIMIZED_VALUE = "maximized";
export const CHAT_RETURN_HISTORY_PARAM = "deskshare-history";
export const CHAT_RETURN_HISTORY_VALUE = "1";

/**
 * Add both markers to a path, preserving whatever query string and hash it
 * already carries.
 */
export function appendChatPostLoginReturnParams(
  pathWithSearch: string,
  maximized = false,
): string {
  const hashIndex = pathWithSearch.indexOf("#");
  const beforeHash =
    hashIndex >= 0 ? pathWithSearch.slice(0, hashIndex) : pathWithSearch;
  const hash = hashIndex >= 0 ? pathWithSearch.slice(hashIndex) : "";
  const queryIndex = beforeHash.indexOf("?");
  const pathname =
    queryIndex >= 0 ? beforeHash.slice(0, queryIndex) : beforeHash;
  const params = new URLSearchParams(
    queryIndex >= 0 ? beforeHash.slice(queryIndex + 1) : "",
  );
  params.set(
    CHAT_RETURN_OPEN_PARAM,
    maximized ? CHAT_RETURN_MAXIMIZED_VALUE : CHAT_RETURN_OPEN_VALUE,
  );
  params.set(CHAT_RETURN_HISTORY_PARAM, CHAT_RETURN_HISTORY_VALUE);
  return `${pathname}?${params.toString()}${hash}`;
}

/**
 * Like getSignInEntryUrl(), but the return destination re-opens the chat
 * panel and its past-conversations drawer after a successful sign-in.
 *
 * PH-1: today nothing reopens the chat after a sign-in. The panel opens from
 * a user action and from nothing else, and the sign-in round trip is a full
 * page navigation - deliberately, so that the chat frame does not survive it
 * holding a token minted for the anonymous visitor. So the panel came back
 * closed and the conversation the customer was reading was two further steps
 * away.
 *
 * The same guard as getSignInEntryUrl: a customer already on a sign-in page
 * gets no redirect at all, which is what stops the value looping.
 */
export function getSignInEntryUrlForChat(
  language: string = "en",
  maximized = false,
): string {
  if (!isDeskshare()) return getDashboardUrl(language);

  const authPath = getAuthLoginUrl();
  const { pathname, search } = window.location;
  if (isSignInFlowPath(pathname)) return authPath;

  const current = appendChatPostLoginReturnParams(
    `${pathname}${search}`,
    maximized,
  );
  return `${authPath}?redirect=${encodeURIComponent(current)}`;
}

/**
 * Generate the add-to-cart URL for a product.
 *
 * @param productId - The product ID to add to cart
 * @param language - The current language code
 * @returns The add-to-cart URL
 */
export function getAddToCartUrl(
  productId: string | number,
  language: string = "en",
): string {
  const storeUrl = getStoreBaseUrl();

  if (isDeskshare()) {
    const langCode = LANG_MAPPER[language] || "eng";
    // Legacy store uses the ASP.NET AddProduct URL regardless of JWT flow.
    return `${getLegacyStoreUrl()}/AddProduct.aspx?ProductId1=${productId}&Lang=${langCode}`;
  }

  return `${storeUrl}/?add-to-cart=${productId}`;
}

/**
 * The cart the customer's items are actually in.
 *
 * Session 383 S-3. Buy Now for a migrated product sends the customer to
 * {shop}/{lang}/add-to-cart, so the header must open {shop}/{lang}/cart/ — or it
 * opens a different shop's empty basket, and when the legacy address is unset it
 * opens a RELATIVE path that this site's own /:productSlug route captures and
 * reports as "This page could not be loaded. Please try refreshing." That is the
 * whole of the reported fault, and "Try Again" can never clear it, because the
 * address is wrong rather than unavailable.
 *
 * getStoreBaseUrl() has a real default and cannot return an empty string, so the
 * relative-path failure cannot happen again through this route.
 *
 * The language is honoured on EVERY host now. It was accepted and then silently
 * discarded on Deskshare hosts, which sent a German customer to an English cart.
 *
 * This helper is corrected rather than the header, because the header is not the
 * only caller: the uninstall-feedback page's Purchase link uses it too, and that
 * one is arguably worse — it is shown to somebody who has just removed a product,
 * at the one moment the site is trying to keep them.
 *
 * @param language - The current language code
 * @returns The cart URL
 */
export function getCartUrl(language: string = "en"): string {
  const storeUrl = getStoreBaseUrl().replace(/\/$/, "");
  return `${storeUrl}/${encodeURIComponent(language)}/cart/`;
}

/**
 * The same destination as getAccountUrl().
 *
 * @deprecated T-2: use getAccountUrl(). After T-2 these two return the same
 * string on every host, so a call site choosing between them is choosing
 * between two identical values. Kept for now so no caller breaks; remove it
 * once SignIn.tsx and RefundPolicy.tsx have moved across.
 *
 * @param language - The current language code
 * @returns The dashboard URL
 */
export function getDashboardUrl(language: string = "en"): string {
  return getShopDashboardUrl(language);
}

/**
 * Generate a bulk add-to-cart URL for multiple items of the same product.
 *
 * @param categoryId - The category/product ID
 * @param count - Number of items
 * @returns The bulk add-to-cart URL
 */
export function getAddToCartBulkUrl(categoryId: number, count: number): string {
  if (isDeskshare()) {
    // Legacy store does not support bulk add-to-cart via a single URL parameter.
    // Use the standard single-product URL with quantity — the store accumulates.
    return `${getLegacyStoreUrl()}/AddProduct.aspx?ProductId1=${categoryId}&Qty=${count}`;
  }

  const storeUrl = getStoreBaseUrl();
  const ids = Array(count).fill(categoryId).join(",");
  return `${storeUrl}/?add-to-cart=${ids}`;
}

// ---------------------------------------------------------------------------
// V3: Buy Now link resolver — routes to the correct store per product type
// ---------------------------------------------------------------------------

/**
 * Input shape for the Buy Now link resolver.
 * All fields are optional so callers don't need to know which branch applies;
 * the resolver picks the correct path from whatever data is available.
 */
export interface PlanLinkInput {
  stripeProductId?: string | null;
  stripePriceId?: string | null;
  legacyCategoryId?: number | null;
  slug?: string | null;
  language?: string;
}

/**
 * Build the real add-to-cart URL for a Stripe product via the new shop.
 *
 * The shop reads productId and priceId from the URL query string. The path
 * segment is kept in this one helper — a single-string change here propagates
 * to every Buy Now link, with no changes needed at call sites (Decision 3).
 */
export function getStripeAddToCartUrl(
  stripeProductId: string,
  stripePriceId: string,
  language?: string,
): string {
  const storeUrl = getStoreBaseUrl();
  const lang = encodeURIComponent(language ?? "en");
  return `${storeUrl}/${lang}/add-to-cart?productId=${encodeURIComponent(stripeProductId)}&priceId=${encodeURIComponent(stripePriceId)}`;
}

/**
 * Determine the real navigation target for a Buy Now click.
 *
 * Priority order (load-bearing — do not reorder):
 *  1. Both Stripe IDs present → new shop add-to-cart. This check runs FIRST
 *     so a migrated product never falls back to the legacy store, even on
 *     a deskshare.com marketing host.
 *  2. Marketing/localhost host with a legacy category ID → legacy ASP.NET store.
 *  3. Non-marketing host with a legacy category ID → new shop (transitional).
 *  4. No usable ID → "#" (disabled state; isCartReady guard prevents activation).
 */
export function getPlanBuyNowUrl(input: PlanLinkInput): string {
  // 1. Migrated product: both Stripe IDs present → new shop.
  if (input.stripeProductId && input.stripePriceId) {
    return getStripeAddToCartUrl(
      input.stripeProductId,
      input.stripePriceId,
      input.language,
    );
  }

  // 2. Marketing / legacy-cart host with a category ID → old ASP.NET store.
  if (isDeskshare() && input.legacyCategoryId != null) {
    const lc = LANG_MAPPER[input.language ?? "en"] || "eng";
    return `${getLegacyStoreUrl()}/AddProduct.aspx?ProductId1=${input.legacyCategoryId}&Lang=${lc}`;
  }

  // 3. Transitional: shop host, product not yet migrated → legacy add-to-cart param.
  if (input.legacyCategoryId != null) {
    return `${getStoreBaseUrl()}/?add-to-cart=${input.legacyCategoryId}`;
  }

  return "#";
}

/**
 * Where a Buy Now control goes, and what kind of destination it is.
 *
 * The single statement of the rule. It was written out separately in the
 * product page, the purchase page and the review page, and the review page
 * fell a version behind without anything failing (Session 429: PH-1, FND-7).
 *
 * Priority order, load-bearing:
 *  1. A custom purchase link set in Django admin.
 *  2. More than one cart row - the purchase page, so a tier can be chosen.
 *  3. One cart row with both Stripe identifiers - straight to the cart.
 *  4. Anything else - the purchase page.
 *
 * `kind` is stated rather than inferred from the text of the address, so a
 * destination added later is a compile error at every call site instead of a
 * silently mismatched branch.
 */
export type BuyNowTarget =
  | { kind: "external"; href: string; target: "_blank" }
  | { kind: "stripe"; href: string; target: "_blank" }
  | { kind: "internal"; href: string; target: "_self" };

export function resolveBuyNowTarget(input: {
  purchasePageLink?: string | null;
  cartItems: CartItem[];
  purchasePagePath: string;
  productSlug?: string;
  language: string;
}): BuyNowTarget {
  const internal: BuyNowTarget = {
    kind: "internal",
    href: input.purchasePagePath,
    target: "_self",
  };

  if (input.purchasePageLink) {
    return { kind: "external", href: input.purchasePageLink, target: "_blank" };
  }

  // No rows at all means the cart request has not answered, or failed. The
  // purchase page is the safe destination: it fetches the cart itself. Stating
  // it as "exactly one row" rather than as two separate guards removes a branch
  // instead of adding one - one row is the only case that can go to the cart.
  if (input.cartItems.length !== 1) return internal;

  const row = input.cartItems[0];
  if (!row.stripe_product_id || !row.stripe_price_id) return internal;

  const href = getPlanBuyNowUrl({
    stripeProductId: row.stripe_product_id,
    stripePriceId: row.stripe_price_id,
    legacyCategoryId: row.catagory_id ?? undefined,
    slug:
      input.productSlug && row.plan
        ? `${input.productSlug}-${row.plan}`
        : undefined,
    // The base code only. The shop's addresses use the two-letter form.
    language: input.language.split("-")[0],
  });

  return href === "#" ? internal : { kind: "stripe", href, target: "_blank" };
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/**
 * Language code mapping for legacy store URLs.
 * Maps ISO language codes to store-specific language parameters.
 */
/**
 * The public path of the Support Forum.
 *
 * One constant, read by every caller. The header and the Technical Support page
 * previously each carried their own copy; the first time the two disagreed they
 * would have sent customers to different places with nothing reporting it.
 *
 * Three things every caller must respect:
 *  - No language prefix. The forum is a separate application reached through a
 *    proxy rule on this exact path. Prefixing it produces a path the proxy does
 *    not match, so React Router handles it and renders our own not-found page.
 *  - A plain anchor, never a router link. React Router never reaches the proxy.
 *  - Leave the trailing shape alone. The proxy rule matches this path exactly.
 */
export const FORUM_PATH = "/forums";

/**
 * Base address of the Support Forum, for validating forum-initiated sign-in
 * redirects (T-7a, FCR Pass 8).
 *
 * Returns FORUM_PATH unchanged (a bare path, not a URL) under today's
 * deployment — the forum is served at /forums/ on this same host (D-4,
 * approved), so safeRedirectUrl's same-origin rule already covers it and
 * callers of this function should check the return value starts with
 * "http" before treating it as a separate origin to validate against.
 *
 * If the forum ever moves to its own host, set
 * VITE_REACT_APP_SUPPORT_FORUM_URL to its full base address and this is the
 * one place that changes — every caller (including safeRedirect.ts and the
 * forum API client) picks it up automatically.
 *
 * A-15: this used to read VITE_REACT_APP_FORUM_URL, a name documented nowhere
 * and therefore always undefined, while .env.example and the Django CSP
 * middleware both used VITE_REACT_APP_SUPPORT_FORUM_URL. Two names for one
 * value meant setting the documented one appeared to do nothing, and the
 * safe-redirect rule written to allow a future forum host was permanently
 * dead. One name now.
 */
export function getForumUrl(): string {
  return import.meta.env.VITE_REACT_APP_SUPPORT_FORUM_URL || FORUM_PATH;
}

export const LANG_MAPPER: Record<string, string> = {
  en: "eng",
  de: "ge",
  fr: "fr",
  es: "sp",
  pt: "po",
  it: "it",
};

/**
 * Returns the Tech Support chatbot base URL for the current environment.
 *
 * Priority order:
 *  1. VITE_TECH_SUPPORT_CHAT_URL  — explicit override (use this on all feature
 *     environments to avoid accidentally pointing at the production chat service)
 *  2. Hostname-based mapping:
 *     - Production (www.deskshare.com / deskshare.com) → production chat
 *     - new.deskshare.com                             → new chat instance
 *     - featureN.deskshare.com / feature.deskshare.com → feature chat instance
 *     - Any other *.deskshare.com subdomain           → test chat instance
 *     - localhost / 127.0.0.1                         → localhost:5173
 *     - private IPv4 (LAN)                            → the SAME host the site
 *                                                       is served from, on
 *                                                       port 5173 (F-4)
 *
 * Query parameters are appended by buildTechSupportChatUrl() in
 * utils/techSupportChat.ts. Do NOT import that function here — it imports this
 * one, and the reverse import would create a cycle.
 *
 * The explicit env override must still be used on feature environments when the
 * hostname mapping is insufficient (e.g. feature3.deskshare.com needs a specific
 * chat service that differs from the generic test instance), and locally when
 * the chat app cannot use port 5173 because this frontend already holds it.
 */
export function getTechSupportChatBaseUrl(): string {
  const override = import.meta.env.VITE_TECH_SUPPORT_CHAT_URL;
  if (override) {
    return String(override).replace(/\/$/, "");
  }

  try {
    const current = new URL(window.location.href);
    const hostname = current.hostname;

    // Local development on the machine itself.
    if (hostname === "localhost" || hostname === "127.0.0.1") {
      return "http://localhost:5173";
    }

    // F-4: a private network address is NOT localhost.
    //
    // This case used to fall in with the two above and return
    // http://localhost:5173, so a tester opening the site on a phone at
    // http://192.168.1.171 was sent to localhost:5173 ON THE PHONE, where
    // nothing is listening. The chat address is built from the same host the
    // site is being served from instead.
    //
    // The protocol comes from the current page deliberately. If the site is
    // served over https on that LAN address the chat address becomes https on
    // port 5173, which a plain development server will not answer — that is
    // the correct behaviour, because a mixed-content address would be blocked
    // by the browser anyway, and it means the explicit override above must be
    // used in that setup.
    //
    // This only works if the AUDIENCE matches. The backend compares the
    // audience exactly against its allow-list, so this exact string — no
    // trailing slash, no path — must be in ALLOWED_JWT_AUDIENCES on the
    // backend that machine talks to, or every mint is refused and the chat
    // silently never recognises anybody. Keep LAN and localhost values in a
    // local-only file; they must never reach a deployed environment's list.
    if (isPrivateIpv4(hostname)) {
      // The protocol comes from the parsed address rather than
      // window.location.protocol: the same object the hostname came from, so
      // the two can never disagree, and no second property of window.location
      // has to exist for this to work.
      return `${current.protocol}//${hostname}:5173`;
    }

    if (
      hostname === "test.deskshare.com" ||
      hostname.endsWith(".test.deskshare.com")
    ) {
      return "https://test.techsupport.deskshare.com";
    }

    if (hostname === "new.deskshare.com") {
      return "https://new.techsupport.deskshare.com";
    }

    // Numbered feature environments (feature1, feature3, ...) get the feature
    // chat service rather than the generic test instance.
    //
    // \\d* rather than \\d+ deliberately: it also matches a host named simply
    // feature.deskshare.com. It costs nothing, it cannot match any other host,
    // and it removes a case where a new environment would silently point at
    // the wrong chat service.
    if (/^feature\d*\.deskshare\.com$/.test(hostname)) {
      return "https://feature.techsupport.deskshare.com";
    }

    // Any other deskshare.com subdomain (feature environments).
    if (
      hostname.endsWith(".deskshare.com") &&
      hostname !== "deskshare.com" &&
      hostname !== "www.deskshare.com"
    ) {
      return "https://test.techsupport.deskshare.com";
    }
  } catch (error) {
    // Parsing window.location.href effectively never throws in a browser, so
    // this is close to unreachable. If it is ever reached on a live production
    // page, sending a customer to a test support instance is the worse outcome,
    // so the fallback below returns production and the failure is reported.
    // Session 383 A-1: through the module, not through a global that this
    // application never creates.
    Sentry.captureException(error);
  }

  // Production, and the safe fallback if hostname parsing throws.
  return "https://techsupport.deskshare.com";
}
